import get from 'lodash/get';
import forEach from 'lodash/forEach';
import keyBy from 'lodash/keyBy';
import flatten from 'lodash/flatten';

import { caseLogicMsg, dialogueMsg } from '../validationMessages';
import { getAllCharacters, targetCharacterKeyOrDefault } from '~lib/common/utils/characters';
import { activeNpcs, getNpcDialogue } from '~lib/common/utils/npc';
import hasActionOrTimerConnection from './validateActionOrTimerConnection';

function validateDialogueControls(caseConf, nodeInfo) {
  const questions = get(caseConf, 'dialogue.questions', []);
  const commands = get(caseConf, 'dialogue.commands', []);
  const unsolicited = get(caseConf, 'dialogue.unsolicited', []);
  const callItems = flatten((get(caseConf, 'dialogue.calls') || []).map(call => call.items))
  const callUnsolicited = flatten((get(caseConf, 'dialogue.calls') || []).map(call => call.unsolicited))
  const questionsById = keyBy(questions, 'id');
  const commandsById = keyBy(commands, 'id');
  const unsolicitedById = keyBy(unsolicited, 'id');
  const callItemsById = keyBy(callItems, 'id');
  const callUnsolicitedById = keyBy(callUnsolicited, 'id');
  const npcQuestions = getNpcDialogue(caseConf).questions || [];
  const npcCommands = getNpcDialogue(caseConf).commands || [];
  const npcUnsolicited = getNpcDialogue(caseConf).unsolicited || [];
  const npcUnsolicitedById = keyBy(npcUnsolicited, 'id');
  const npcQuestionsById = keyBy(npcQuestions, 'id');
  const npcCommandsById = keyBy(npcCommands, 'id');
  const characters = getAllCharacters(caseConf);
  const charactersByKey = keyBy(characters, 'key');

  const errors = [];
  const warnings = [];

  const dialogueItemsByTypeById = {
    QUESTION: questionsById,
    ANSWER: questionsById,
    COMMAND: commandsById,
    COMMAND_COUNT: commandsById,
    UNSOLICITED_DIALOGUE: unsolicitedById,
    UNSOLICITED_CALL: callUnsolicitedById,
    CALL: callItemsById,
    CALL_ANSWER: callItemsById,
    NPC_UNSOLICITED_DIALOGUE: npcUnsolicitedById,
    NPC_QUESTION: npcQuestionsById,
    NPC_COMMAND: npcCommandsById,
    NPC_COMMAND_COUNT: npcCommandsById,
    NPC_ANSWER: npcQuestionsById,
  };
  const dialogueTitleByType = {
    QUESTION : 'Question',
    ANSWER: 'Question',
    COMMAND: 'Command',
    COMMAND_COUNT: 'Command',
    UNSOLICITED_DIALOGUE: 'Unsolicited',
    UNSOLICITED_CALL: 'Unsolicited',
    CALL: 'Call dialogue',
    CALL_ANSWER: 'Call dialogue',
    NPC_UNSOLICITED_DIALOGUE: 'VIP Unsolicited',
    NPC_QUESTION: 'VIP Question',
    NPC_COMMAND: 'VIP Command',
    NPC_COMMAND_COUNT: 'VIP Command',
    NPC_ANSWER: 'VIP Question',
  };
  const dialogueArticleByType = {
    QUESTION : 'a',
    ANSWER: 'a',
    COMMAND: 'a',
    COMMAND_COUNT: 'a',
    UNSOLICITED_DIALOGUE: 'an',
    UNSOLICITED_CALL: 'an',
    CALL: 'a',
    CALL_ANSWER: 'a',
    NPC_UNSOLICITED_DIALOGUE: 'a',
    NPC_QUESTION: 'a',
    NPC_COMMAND: 'a',
    NPC_COMMAND_COUNT: 'a',
    NPC_ANSWER: 'a',
  };

  const npcs = activeNpcs(caseConf);
  const npcsByKey = keyBy(npcs, 'key');

  // Action nodes
  const dialogueActionTypes = ['QUESTION', 'COMMAND', 'CALL', 'NPC_QUESTION', 'NPC_COMMAND'];

  nodeInfo.actionNodes.forEach((node) => {
    const action = get(node, 'data.action');
    if (action && dialogueActionTypes.includes(action.type)) {
      const dialogueTitle = dialogueTitleByType[action.type];
      const dialogueArticle = dialogueArticleByType[action.type];
      if (!action.id) {
        errors.push(caseLogicMsg(`You need to select ${dialogueArticle} ${dialogueTitle.toLowerCase().replace('vip', 'VIP')}`, node.id, { errorId: 'missingQuestion' }));
        return;
      }
      const dialogueItem = dialogueItemsByTypeById[action.type][action.id];
      if (!dialogueItem) {
        errors.push(caseLogicMsg(`${dialogueTitle} could not be found`, node.id, { errorId: 'invalidQuestion' }));
      }
    }
  });

  const dialogueActionTypesWithCharacter = ['NPC_QUESTION', 'NPC_COMMAND'];

  nodeInfo.actionNodes.forEach((node) => {
    const action = get(node, 'data.action');
    if (action && dialogueActionTypesWithCharacter.includes(action.type)) {
      if (!action.npcKey) {
        errors.push(caseLogicMsg('You need to select a VIP', node.id, { errorId: 'missingSourceCharacter' }));
        return;
      }
      if (action.npcKey && !npcsByKey[action.npcKey]) {
        errors.push(caseLogicMsg('You need to select a VIP', node.id, { errorId: 'missingSourceCharacter' }));
        return;
      }
    }
  });

  // Effect nodes
  const dialogueEffectTypes = ['QUESTION', 'ANSWER', 'COMMAND', 'CALL', 'CALL_ANSWER', 'UNSOLICITED_DIALOGUE', 'UNSOLICITED_CALL', 'NPC_QUESTION', 'NPC_COMMAND', 'NPC_ANSWER', 'NPC_UNSOLICITED_DIALOGUE'];
  const dialogueEffectTypesWithCharacter = ['NPC_QUESTION', 'NPC_COMMAND', 'NPC_ANSWER', 'NPC_UNSOLICITED_DIALOGUE'];
  const connectedUnsolicited = [];

  nodeInfo.effectNodes.forEach((node) => {
    const effect = get(node, 'data.effect');
    if (effect && dialogueEffectTypes.includes(effect.type)) {
      const dialogueTitle = dialogueTitleByType[effect.type];
      const dialogueArticle = dialogueArticleByType[effect.type];
      if (!effect.id) {
        errors.push(caseLogicMsg(`You need to select ${dialogueArticle} ${dialogueTitle.toLowerCase().replace('vip', 'VIP')}`, node.id, { errorId: 'missingQuestion' }));
        return;
      }
      const dialogueItem = dialogueItemsByTypeById[effect.type][effect.id];
      if (!dialogueItem) {
        errors.push(caseLogicMsg(`${dialogueTitle} could not be found`, node.id, { errorId: 'invalidQuestion' }));
      } else if (effect.type === 'UNSOLICITED_DIALOGUE') {
        if (!dialogueItem.options.answer || !dialogueItem.options.answer.en) {
          errors.push(dialogueMsg('Empty unsolicited item', dialogueItem.id, { errorId: 'emptyUnsolicited', dialogueType: 'UNSOLICITED' }));
        }
      }
      if (dialogueEffectTypesWithCharacter.includes(effect.type)) {
        if (!effect.npcKey) {
          errors.push(caseLogicMsg('You need to select a VIP', node.id, { errorId: 'missingSourceCharacter' }));
          return;
        }
        if (effect.npcKey && !npcsByKey[effect.npcKey]) {
          errors.push(caseLogicMsg('You need to select a VIP', node.id, { errorId: 'missingSourceCharacter' }));
          return;
        }
        if (effect.type === 'NPC_UNSOLICITED_DIALOGUE') {
          if (!targetCharacterKeyOrDefault(effect.targetCharacterKey)) {
            errors.push(caseLogicMsg('You need to select a Target Character', node.id, { errorId: 'missingTargetCharacter' }));
          }
        }
      }
      if (['UNSOLICITED_DIALOGUE', 'NPC_UNSOLICITED_DIALOGUE'].includes(effect.type)) {
        // need to track if unsolicited dialogue is used by an effect for a check validation later
        if (hasActionOrTimerConnection(node, nodeInfo.nodeMap)) {
          connectedUnsolicited.push({ id: effect.id, targetCharacterKey: targetCharacterKeyOrDefault(effect.targetCharacterKey) });
        }
      }
      if (effect.type === 'UNSOLICITED_DIALOGUE') {
        const targetCharacterKey = targetCharacterKeyOrDefault(effect.targetCharacterKey);
        if (!targetCharacterKey) {
          errors.push(caseLogicMsg('You need to select a Target Character', node.id, { errorId: 'missingTargetCharacter' }));
        }
        if (targetCharacterKey && !charactersByKey[targetCharacterKey]) {
          errors.push(caseLogicMsg('Target character could not be found', node.id, { errorId: 'missingTargetCharacter' }));
        }
      }
    }
  });


  // Check nodes
  const dialogueCheckTypes = ['QUESTION', 'COMMAND_COUNT', 'UNSOLICITED_DIALOGUE', 'CALL', 'UNSOLICITED_DIALOGUE', 'UNSOLICITED_CALL', 'NPC_UNSOLICITED_DIALOGUE', 'NPC_QUESTION', 'NPC_COMMAND_COUNT'];
  const dialogueCheckTypesWithCharacter = ['NPC_UNSOLICITED_DIALOGUE', 'NPC_QUESTION', 'NPC_COMMAND_COUNT'];

  nodeInfo.checkNodes.forEach((node) => {
    forEach(node.data, (check, checkKey) => {
      if (dialogueCheckTypes.includes(check.type)) {
        const dialogueTitle = dialogueTitleByType[check.type];
        const dialogueArticle = dialogueArticleByType[check.type];
        if (!check.id) {
          errors.push(caseLogicMsg(`You need to select ${dialogueArticle} ${dialogueTitle.toLowerCase().replace('vip', 'VIP')}`, node.id, { checkKey }));
          return;
        }
        const dialogueItem = dialogueItemsByTypeById[check.type][check.id];
        if (!dialogueItem) {
          errors.push(caseLogicMsg(`${dialogueTitle} could not be found`, node.id, { checkKey }));
        }
        if (check.type === 'UNSOLICITED_DIALOGUE' || check.type === 'NPC_UNSOLICITED_DIALOGUE') {
          // null = new node with no selection (error); undefined = legacy node, defaults to User
          if (check.targetCharacterKey === null) {
            errors.push(caseLogicMsg('You need to select a Target Character', node.id, { checkKey, errorId: 'missingTargetCharacter' }));
          } else if (check.targetCharacterKey && !charactersByKey[check.targetCharacterKey]) {
            errors.push(caseLogicMsg('Target character could not be found', node.id, { checkKey, errorId: 'missingTargetCharacter' }));
          }
        }
        if (dialogueCheckTypesWithCharacter.includes(check.type)) {
          if (!check.npcKey) {
            errors.push(caseLogicMsg('You need to select a VIP', node.id, { checkKey, errorId: 'missingSourceCharacter' }));
            return;
          }
          if (check.npcKey && !npcsByKey[check.npcKey]) {
            errors.push(caseLogicMsg('You need to select a VIP', node.id, { checkKey, errorId: 'missingSourceCharacter' }));
            return;
          }
        }
        if (['UNSOLICITED_DIALOGUE', 'NPC_UNSOLICITED_DIALOGUE'].includes(check.type)) {
          const checkTargetCharacterKey = targetCharacterKeyOrDefault(check.targetCharacterKey);
          if (!connectedUnsolicited.some(c => c.id === check.id && c.targetCharacterKey === checkTargetCharacterKey)) {
            errors.push(caseLogicMsg(`Use this ${dialogueTitle} in an Effect with same Target Character, connected to an Action or Timer`, node.id, { checkKey, errorId: 'invalidUnsolicited', checkId: check.id }));
          }
        }
      }
    });
  });

  return {
    errors,
    warnings,
  };
}

export default validateDialogueControls;
